import { createDownloadJob, getDownloadJobs } from "@modules/downloadJobs";
import { setNotification } from "@modules/notification";
import DownloadInfoMessage from "@routes/share/ui/DownloadInfoMessage";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import useActionPending from "@hooks/useActionPending";

const useDownloadJobs = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { pending: downloadJobPending, setActionPending, disableActionPending } = useActionPending();

    const generateDownloadJobHash = (arr) => {
        if (!arr || !Array.isArray(arr) || arr.length === 0) return "";

        const sortedArray = [...arr].sort();
        const str = sortedArray.join("|");

        // 해시 함수 (djb2 알고리즘 변형)
        let hash = 5381;
        for (let i = 0; i < str.length; i++) {
            hash = (hash << 5) + hash + str.charCodeAt(i);
        }
        return Math.abs(hash).toString(36);
    };

    const toastRequestStartMessage = () => {
        dispatch(
            setNotification({
                type: "info",
                contents: t("common::msg::Processing download request...", "Processing download request..."),
            }),
        );
    };

    const toastInfoMessage = (folders, fileName, totalCount, isDirectDownloadRequest = false) => {
        dispatch(
            setNotification({
                type: "info",
                contents: (
                    <DownloadInfoMessage
                        folders={folders}
                        fileName={fileName}
                        totalCount={totalCount}
                        isDirectDownloadMissMatchRegion={isDirectDownloadRequest}
                    />
                ),
            }),
        );
    };

    const requestDownloadJob = async (domain, obj, targetsCount, options, jobProjectId, uniqueKey) => {
        let dynamicKey = `${domain}_${obj.resourceId}_${uniqueKey ?? "single"}`;

        if (downloadJobPending[dynamicKey]) {
            throw new Error("duplicated download request");
        }
        try {
            setActionPending(dynamicKey);

            toastRequestStartMessage(obj.resourceName, targetsCount);

            const response = await dispatch(createDownloadJob(domain, obj, options, jobProjectId));

            toastInfoMessage("", obj.resourceName, targetsCount, false);

            const jobId = response?.data?.id;
            await dispatch(getDownloadJobs([jobId]));
        } catch (error) {
            console.trace(error);
            return Promise.reject(error);
        } finally {
            disableActionPending(dynamicKey);
        }
    };

    const requestMultiProjectDownloadJob = async (groupedByProjectId) => {
        try {
            const downloadResult = await Promise.allSettled(
                Object.entries(groupedByProjectId).map(async ([projectId, items], index) => {
                    try {
                        const downloadRequestTargets = {
                            resourceId: items.length === 1 ? items[0].id : undefined,
                            resourceIds: items.length > 1 ? items.map((item) => item.id) : undefined,
                            projectId: projectId,
                            resourceName: items[0].name,
                        };
                        await requestDownloadJob("ASSET", downloadRequestTargets, items.length, false);
                        return items;
                    } catch (error) {
                        return Promise.reject(error);
                    }
                }),
            );

            return downloadResult
                .filter(({ status }) => status === "fulfilled")
                ?.reduce((acc, cur) => acc.concat(cur.value), []);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    return {
        generateDownloadJobHash,
        requestDownloadJob,
        requestMultiProjectDownloadJob,
    };
};

export default useDownloadJobs;
